import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { apiService } from "../services/api";
import { JobSubmission } from "../types";
import InlineFeedback from "../components/InlineFeedback";
import { useFeedback } from "../hooks/useFeedback";
import JSZip from "jszip";

const formatFileSize = (bytes: number) => {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const NewJob: React.FC = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [isZipping, setIsZipping] = useState(false);
  const feedback = useFeedback();
  const [formData, setFormData] = useState<JobSubmission>({
    project_name: "",
    report_format: "xlsx",
    source_dialect: "",
    parameters: {},
  });

  const ALLOWED_EXTENSIONS = ['.sas', '.spk', '.egp', '.zip', '.tar', '.gz'];
  const ACCEPT_ATTRIBUTE = ALLOWED_EXTENSIONS.join(',');
  
  const isValidFileType = (file: File): boolean => {
    const fileName = file.name.toLowerCase();
    return ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext));
  };

  const mutation = useMutation({
    mutationFn: (data: { file: File; submission: JobSubmission }) => apiService.submitJob(data.file, data.submission),
    onSuccess: (response) => {
      navigate(`/jobs/${response.job_id}`);
    },
    onError: (error: any) => {
      feedback.showError(error.response?.data?.message || "Failed to submit job. Try again.");
    },
  });

  const generateJobNameSuggestion = (selectedFiles: File[]): string => {
    if (selectedFiles.length === 0) return "";
    
    if (selectedFiles.length === 1) {
      const fileName = selectedFiles[0].name;
      const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
      // Remove any quotes and special characters that might cause issues
      return nameWithoutExt.replace(/['"]/g, '').trim();
    }
    
    const commonPrefix = selectedFiles
      .map(f => f.name.substring(0, f.name.lastIndexOf('.')) || f.name)
      .reduce((acc, name) => {
        if (!acc) return name;
        let i = 0;
        while (i < acc.length && i < name.length && acc[i] === name[i]) i++;
        return acc.substring(0, i);
      });
    
    if (commonPrefix.length > 3) {
      const cleanPrefix = commonPrefix.replace(/['"]/g, '').trim();
      return `${cleanPrefix}_${selectedFiles.length}_files`;
    }
    
    return `Analysis_${selectedFiles.length}_files`;
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const resetFileInput = () => {
    const fileInput = document.getElementById('file-upload') as HTMLInputElement | null;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const invalidExtensionsMessage = (invalidFiles: File[]): string => {
    const invalidExtensions = Array.from(new Set(
      invalidFiles.map(f => {
        const lastDot = f.name.lastIndexOf('.');
        return lastDot >= 0 ? f.name.slice(lastDot).toLowerCase() : 'no extension';
      })
    ));
    return `Invalid file extensions: ${invalidExtensions.join(', ')}.\nUse only ${ALLOWED_EXTENSIONS.join(', ')} files.`;
  };

  // Rejects the whole selection when any file has an unsupported extension.
  const selectFiles = (fileList: File[]) => {
    feedback.clear();
    const invalidFiles = fileList.filter(file => !isValidFileType(file));
    if (invalidFiles.length > 0) {
      feedback.showError(invalidExtensionsMessage(invalidFiles));
      resetFileInput();
      return;
    }
    setFiles(fileList);
    if (!formData.project_name) {
      setFormData({ ...formData, project_name: generateJobNameSuggestion(fileList) });
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      selectFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      selectFiles(Array.from(e.target.files));
    }
  };

  const handleClearFiles = () => {
    setFiles([]);
    resetFileInput();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    feedback.clear();
    if (files.length === 0) {
      feedback.showError("Select at least one file.");
      return;
    }

    let fileToSubmit: File;
    
    if (files.length === 1) {
      // Single file - submit as is
      fileToSubmit = files[0];
    } else {
      // Multiple files - create a zip
      setIsZipping(true);

      try {
        const zip = new JSZip();
        
        // Add each file to the zip
        for (const file of files) {
          const arrayBuffer = await file.arrayBuffer();
          zip.file(file.name, arrayBuffer);
        }
        
        // Generate the zip file
        const zipBlob = await zip.generateAsync({ type: "blob" });
        
        // Create a File object from the blob
        const zipFileName = formData.project_name 
          ? `${formData.project_name.replace(/[^a-zA-Z0-9-_]/g, '_')}.zip`
          : `analysis_${Date.now()}.zip`;
        
        fileToSubmit = new File([zipBlob], zipFileName, { type: "application/zip" });
      } catch (error) {
        feedback.showError("Failed to create zip file.");
        console.error("Zip creation error:", error);
        return;
      } finally {
        setIsZipping(false);
      }
    }
    
    // Sanitize project name by replacing spaces with underscores
    const sanitizedFormData = {
      ...formData,
      project_name: formData.project_name.replace(/\s+/g, '_')
    };
    
    mutation.mutate({ file: fileToSubmit, submission: sanitizedFormData });
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-6" aria-label="New analysis">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">New Analysis</h2>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={files.length === 0 || !formData.project_name.trim() || isZipping || mutation.isPending}
              className="btn-primary font-medium"
            >
              {isZipping ? "Creating zip..." : mutation.isPending ? "Submitting..." : "Submit Job"}
            </button>
          </div>
        </div>

        <InlineFeedback feedback={feedback.feedback} />

        <div className="bg-widget-bg rounded-lg border border-secondary p-6">
          <h3 className="text-lg font-semibold mb-4">Upload Source Files</h3>

          <div className="mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-sm text-body-color flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span>
                <strong>Your privacy:</strong> We process your files securely. We delete them right after the analysis. We never store or share your source code.
              </span>
            </p>
          </div>

          <div
            data-testid="drop-zone"
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? "border-primary bg-primary/10" : "border-secondary"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input type="file" id="file-upload" aria-label="Source files" className="hidden" onChange={handleFileChange} accept={ACCEPT_ATTRIBUTE} multiple />
            <label htmlFor="file-upload" className="cursor-pointer">
              {files.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-heading font-medium">
                      {files.length} file{files.length > 1 ? 's' : ''} selected
                    </p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleClearFiles();
                      }}
                      className="text-sm text-secondary hover:text-body-color transition-colors"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {files.map((file, index) => (
                      <div key={index} className="flex justify-between items-center text-sm">
                        <span className="text-body-color truncate max-w-[70%]">{file.name}</span>
                        <span className="text-secondary">{formatFileSize(file.size)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-secondary text-sm mt-2">
                    Total size: {formatFileSize(files.reduce((acc, f) => acc + f.size, 0))}
                  </p>
                  {files.length > 1 && (
                    <p className="text-primary text-sm">
                      We zip these files before upload.
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-body-color">Drag and drop your source files here, or click to browse.</p>
                  <p className="text-secondary text-sm mt-2">Supported file extensions: {ALLOWED_EXTENSIONS.join(', ')}. Maximum upload size: 1 GB.</p>
                  <p className="text-secondary text-sm">You can select multiple files. If you add more than one .spk file, we process only one.</p>
                </div>
              )}
            </label>
          </div>
        </div>

        <div className="bg-widget-bg rounded-lg border border-secondary p-6">
          <h3 className="text-lg font-semibold mb-4">Job Configuration</h3>

          <div>
            <label htmlFor="project-name" className="block text-sm font-medium text-secondary mb-2">Project Name</label>
            <input
              id="project-name"
              type="text"
              value={formData.project_name}
              onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
              className="w-full bg-body-bg border border-secondary rounded-md px-3 py-2 text-body-color focus:outline-none focus:border-primary"
              placeholder="For example: Migration Q4"
              required
            />
            {formData.project_name && formData.project_name.includes(' ') && (
              <p className="text-xs text-secondary mt-1">
                We replace spaces with underscores: <span className="text-primary">{formData.project_name.replace(/\s+/g, '_')}</span>
              </p>
            )}
          </div>
        </div>

      </form>
    </div>
  );
};

export default NewJob;
